import { useEffect } from "react";
import { startFightLivingWorkerBridge } from "./FightLivingWorkerBridge";
import { createBudgetedRuntimeLoop, installBrowserNexusFederation } from "@inyron/living-universe";
import { HarmonicFieldRuntime } from "@inyron/animation";
import { arenas, fighters } from "./content";
import { lifeTelemetry } from "./lifeOmega";
import {
  activateFighter,
  fightLifeId,
  fightLivingPersistence,
  fightLivingUniverse,
  seedFightRoster,
} from "./livingUniverse";
import { useFightStore } from "./store";

function localLivingQaMode(){
  if(typeof window==='undefined')return null
  if(window.location.hostname!=='127.0.0.1'&&window.location.hostname!=='localhost')return null
  return new URLSearchParams(window.location.search).get('qaLivingMode')
}
const harmonicRuntimeByFighter = new Map<string, HarmonicFieldRuntime>();
const harmonicRuntime = (id: string) => {
  let runtime = harmonicRuntimeByFighter.get(id);
  if (!runtime) {
    runtime = new HarmonicFieldRuntime(`fight:living:${id}`, { quality: "medium", maxOrder: 16, sampleRateHz: 120 });
    harmonicRuntimeByFighter.set(id, runtime);
  }
  return runtime;
};

export function FightLivingUniverseBridge() {
  useEffect(() => {
    const qaMode=localLivingQaMode()
    // Production: the full persistent universe runs off the render/input thread.
    // Keep existing QA modes as independent comparisons and a compatibility fallback.
    if(qaMode===null&&typeof Worker!=='undefined'){
      try{return startFightLivingWorkerBridge()}
      catch(error){console.error('[Fight Living Worker] startup fallback',error)}
    }
    if(qaMode==='restoreOnly'){ fightLivingPersistence.restore(Date.now()); return }
    if(qaMode==='seedOnly'){ seedFightRoster(); return }
    if(qaMode==='federationOnly'){ const f=installBrowserNexusFederation(fightLivingUniverse); return()=>f.stop() }
    if(qaMode==='tickOnly'){ const timer=window.setInterval(()=>fightLivingUniverse.tick(.25,Date.now()),250); return()=>window.clearInterval(timer) }
    const skipTick=qaMode==='noTick'
    fightLivingPersistence.restore(Date.now());
    seedFightRoster();
    const nexusFederation = installBrowserNexusFederation(fightLivingUniverse);

    const sync = (
      state: ReturnType<typeof useFightStore.getState>,
      prev?: ReturnType<typeof useFightStore.getState>,
    ) => {
      const playerDef = fighters[state.playerIndex];
      const enemyDef = fighters[state.enemyIndex];
      const arena = arenas[state.arenaIndex];
      if (!playerDef || !enemyDef || !arena) return;

      if (!prev || state.playerIndex !== prev.playerIndex || state.enemyIndex !== prev.enemyIndex || state.arenaIndex !== prev.arenaIndex) {
        activateFighter(playerDef.id, arena.id);
        activateFighter(enemyDef.id, arena.id);
      }

      if (prev && state.impactSerial !== prev.impactSerial && state.impactCue) {
        const attacker = state.impactCue.source === "player" ? playerDef : enemyDef;
        const defender = state.impactCue.source === "player" ? enemyDef : playerDef;
        fightLivingUniverse.observe({
          entityId: fightLifeId(defender.id),
          kind: "threat",
          targetId: fightLifeId(attacker.id),
          intensity: state.impactCue.kind === "guard-break" ? 0.85 : 0.65,
          summary: `combat-impact:${state.impactCue.kind}:${state.impactCue.attackKind ?? "unknown"}`,
        });
        fightLivingUniverse.nexus.publish({
          atMs: Date.now(), gameId: fightLivingUniverse.gameId, worldId: fightLivingUniverse.worldId,
          type: "fight-physiology-envelope", source: "fight-life-omega",
          payload: { impactSerial: state.impactSerial, impactKind: state.impactCue.kind,
            attackKind: state.impactCue.attackKind, attackerId: attacker.id,
            player: { fighterId: playerDef.id, telemetry: lifeTelemetry(state.playerLife, "T0") },
            enemy: { fighterId: enemyDef.id, telemetry: lifeTelemetry(state.enemyLife, "T0") } },
        });
      }

      if (prev && state.status === "match-over" && prev.status !== "match-over") {
        const winner = state.winner === "player" ? playerDef : state.winner === "enemy" ? enemyDef : null;
        const loser = state.winner === "player" ? enemyDef : state.winner === "enemy" ? playerDef : null;
        if (winner && loser) {
          const winnerId = fightLifeId(winner.id);
          const loserId = fightLifeId(loser.id);
          fightLivingUniverse.observe({
            entityId: winnerId,
            kind: "success",
            intensity: 1,
            targetId: loserId,
            summary: `match-victory:${arena.id}`,
          });
          fightLivingUniverse.observe({
            entityId: loserId,
            kind: "failure",
            intensity: 1,
            targetId: winnerId,
            summary: `match-defeat:${arena.id}`,
          });
          fightLivingUniverse.relate(winnerId, loserId, "rivalry", 0.5, "fight rivalry");
          fightLivingUniverse.relate(loserId, winnerId, "rivalry", 0.55, "fight rivalry");
        }

        fightLivingUniverse.nexus.publish({
          atMs: Date.now(),
          gameId: fightLivingUniverse.gameId,
          worldId: fightLivingUniverse.worldId,
          type: "fight-match-complete",
          source: "fight-raw",
          payload: { player: playerDef.id, enemy: enemyDef.id, arena: arena.id, winner: state.winner },
        });
        fightLivingPersistence.save(Date.now());
      }
    };

    sync(useFightStore.getState());
    const unsubscribe = useFightStore.subscribe((state, prev) => sync(state, prev));
    const project = (
      side: "player" | "enemy",
      def: (typeof fighters)[number] | undefined,
      combatant: ReturnType<typeof useFightStore.getState>["player"],
      now: number,
    ) => {
      if (!def) return;
      const health01 = Math.max(0, Math.min(1, combatant.health / Math.max(1, combatant.maxHealth)));
      const stamina01 = Math.max(0, Math.min(1, combatant.stamina / 100));
      const attackEnergy = combatant.attackPhase === "active" ? 1 : combatant.attackPhase ? .62 : .18;
      const frame = harmonicRuntime(def.id).sample(now / 1000, {
        energy01: Math.max(0, Math.min(1, attackEnergy * .45 + combatant.superMeter / 100 * .3 + stamina01 * .25)),
        tension01: Math.max(0, Math.min(1, (1 - health01) * .5 + Math.min(1, combatant.hitstun * 4) * .3 + (combatant.blocking ? .2 : 0))),
        coherence01: Math.max(.45, Math.min(1, .72 + (combatant.parryWindow > 0 ? .18 : 0) + health01 * .1)),
        detail01: .68,
        coupling01: combatant.attackPhase ? .64 : .32,
        phaseOffset01: side === "player" ? .19 : .69,
      });
      fightLivingUniverse.ingestHarmonicProjection(frame, {
        id: `fight:harmonic:${side}`,
        sourceId: fightLifeId(def.id),
        domain: "combat",
        position: { x: combatant.x, y: combatant.z },
      });
    };

    let harmonicPhase = 0;
    let previousUniverseTick = performance.now();
    const livingLoop = createBudgetedRuntimeLoop(() => {
      const now = performance.now();
      const state = useFightStore.getState();
      if (harmonicPhase === 0) {
        project("player", fighters[state.playerIndex], state.player, now);
      } else if (harmonicPhase === 1) {
        project("enemy", fighters[state.enemyIndex], state.enemy, now);
      } else if (!skipTick) {
        fightLivingUniverse.tick(
          Math.min(1, Math.max(0, (now - previousUniverseTick) / 1000)),
          Date.now(),
        );
        previousUniverseTick = now;
      }
      harmonicPhase = (harmonicPhase + 1) % 3;
    }, {
      intervalMs: 350,
      idleTimeoutMs: 120,
      maxTaskMs: 12,
      label: "fight-living-universe",
    });
    livingLoop.start();
    const saveTimer = window.setInterval(() => fightLivingPersistence.save(Date.now()), 10_000);
    const persist = () => fightLivingPersistence.save(Date.now());
    window.addEventListener("pagehide", persist);
    document.addEventListener("visibilitychange", persist);

    return () => {
      livingLoop.stop();
      window.clearInterval(saveTimer);
      window.removeEventListener("pagehide", persist);
      document.removeEventListener("visibilitychange", persist);
      nexusFederation.stop();
      unsubscribe();
    };
  }, []);

  return null;
}
